// Feed brand kit -> creative template brand.
//
// Why this exists in shared rather than at either call site:
//
// The feed document stores a brand kit in product language (`mainColor`,
// `secondaryColor`, `logoEnabled`, `mainFont`). The templates take design
// language (`accent`, `ink`, `scrim`, `showLogo`, `fontDisplay`). Two
// consumers need that translation and they MUST agree:
//
//   1. the manager's feed preview (FeedEditPage)
//   2. the headless export (api/routes/feeds.ts -> /render/:template)
//
// They previously disagreed: the preview mapped every field inline while
// the export handed `feed.brand` to the template untranslated, so every
// key silently missed and the export fell back to DEFAULT_BRAND. A feed
// branded #6C5CE7 exported with the stock #408AF7 accent — no error, just
// wrong creative. Sharing components is not enough when the props going
// into them are built twice; this is the other half of that guarantee.
//
// Keep this pure and dependency-free — it runs in the browser and in the
// API, and its output is hashed into the creative cache key.

/** An uploaded typeface. `family` is generated, never filename-derived. */
export interface UploadedFontFace {
  family: string;
  url: string;
}

/** The brand kit as stored on a feed document. */
export interface FeedBrandKit {
  logoUrl?: string | null;
  logoEnabled?: boolean;
  logoSize?: number;
  qrEnabled?: boolean;
  qrSize?: number;
  /**
   * QR placement, normalised 0–1 against the creative's PHOTO area.
   *
   * One per ratio: the square and the vertical put the code in visibly
   * different places (the vertical's hero is shorter than its canvas, and
   * its bottom stack sits higher), so a single shared value lands well in
   * one of them and badly in the other.
   *
   * null = the template's designed default for that ratio.
   */
  qrPosition?: { x: number; y: number } | null;
  qrPositionVertical?: { x: number; y: number } | null;
  mainColor?: string;
  secondaryColor?: string;
  /** CSS stack, used when the matching *Face is null. */
  mainFont?: string | null;
  secondaryFont?: string | null;
  /** Uploaded faces. When set, these win over the stacks above. */
  mainFontFace?: UploadedFontFace | null;
  secondaryFontFace?: UploadedFontFace | null;
}

/**
 * Template-facing brand props. A superset of both templates' brand
 * interfaces — the banner reads `fontDisplay`, the lookbook reads
 * `fontSans`, and each ignores the other. Spread this over the template's
 * own DEFAULT_BRAND so unset fields keep their designed values.
 *
 * `radius` is deliberately absent: it is the consumer's call (0 for
 * export, ~28 for the in-app preview), not part of the brand kit.
 */
export interface TemplateBrandProps {
  accent: string;
  ink: string;
  scrim: string;
  logoUrl: string | null;
  showLogo: boolean;
  logoSize: number;
  showQr: boolean;
  qrSize: number;
  qrPosition: { x: number; y: number } | null;
  fontDisplay: string;
  fontSans: string;
  fontMono: string;
}

// Fallbacks match the templates' own defaults, so an unset field looks
// the same whether it was omitted here or never mapped at all.
const DEFAULT_MAIN_COLOR = '#408AF7';
const DEFAULT_INK = '#0B0B0C';
const DEFAULT_SANS = "'Geist', system-ui, -apple-system, sans-serif";
const DEFAULT_DISPLAY = "'Jost', system-ui, sans-serif";
const DEFAULT_MONO = "'Geist Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

/** A stack that puts an uploaded family first, with a sane fallback. */
function faceStack(
  face: UploadedFontFace | null | undefined,
  stack: string | null | undefined,
  fallback: string,
  generic: string,
): string {
  if (face?.family) return `'${face.family}', ${generic}`;
  return stack || fallback;
}

export interface FeedBrandOptions {
  /**
   * The agency's own logo, used when the feed sets no override. The feed's
   * `logoUrl` is a per-feed override and never writes back to the agency.
   */
  agencyLogo?: string | null;
  /**
   * Which canvas these props are for. Selects the matching QR placement;
   * everything else in the kit is ratio-independent.
   */
  ratio?: '1:1' | '9:16';
}

export function feedBrandToTemplateBrand(
  brand: FeedBrandKit | null | undefined,
  { agencyLogo = null, ratio = '1:1' }: FeedBrandOptions = {},
): TemplateBrandProps {
  const b = brand ?? {};
  const main = b.mainColor || DEFAULT_MAIN_COLOR;
  const secondary = b.secondaryColor || DEFAULT_INK;
  return {
    accent: main,
    ink: secondary,
    // The handoff fixes the scrim's alpha ramp and calls it load-bearing;
    // only the hue is themable, which is what `secondaryColor` sets here.
    scrim: secondary,
    logoUrl: b.logoUrl ?? agencyLogo,
    showLogo: b.logoEnabled ?? true,
    logoSize: b.logoSize ?? 88,
    // On by default — Dubai requires the permit QR on advertised creative.
    showQr: b.qrEnabled ?? true,
    qrSize: b.qrSize ?? 132,
    qrPosition:
      (ratio === '9:16' ? b.qrPositionVertical : b.qrPosition) ?? null,
    fontDisplay: faceStack(
      b.mainFontFace,
      b.mainFont,
      DEFAULT_DISPLAY,
      'system-ui, sans-serif',
    ),
    fontSans: faceStack(
      b.mainFontFace,
      b.mainFont,
      DEFAULT_SANS,
      'system-ui, sans-serif',
    ),
    fontMono: faceStack(
      b.secondaryFontFace,
      b.secondaryFont,
      DEFAULT_MONO,
      'ui-monospace, monospace',
    ),
  };
}

/**
 * The uploaded faces the render page must register via the FontFace API
 * before it signals `__creativeReady`. Google/system stacks are absent by
 * design — those resolve from the page's own stylesheets.
 */
export function feedBrandFonts(
  brand: FeedBrandKit | null | undefined,
): UploadedFontFace[] {
  const b = brand ?? {};
  const faces = [b.mainFontFace, b.secondaryFontFace].filter(
    (f): f is UploadedFontFace => !!f?.family && !!f?.url,
  );
  // De-duplicate: one file used for both slots must not be loaded twice.
  return faces.filter(
    (f, i) => faces.findIndex((o) => o.family === f.family) === i,
  );
}
